import { ref } from 'vue'
import { Brand, brand } from '@/brand/brand'
import i18n from '@/languages/i18n'
import { apiRequest, setAuthExpiredHandler } from '@/service/api/api_client'
import { ApiError } from '@/service/api/api_error'
import { zIndexLayers } from '@/service/ui/z_index'
import { IS_INTERNAL_BUILD } from '@libs/utils/envUtils'
import type { AuthState, DriverLoginResponse } from './auth_types'

const AUTH_STORAGE_KEY = 'driverUserAuthState'
const DEFAULT_USER_CENTER_APP_ID = '1f764619da1e5f8e8a630a4c53d83037'
const LOGIN_TIMEOUT_MS = 5 * 60 * 1000
const AUTH_RECOVERY_LOGOUT_TIMEOUT_MS = 1500
const USER_CENTER_CLOSE_MESSAGE_TYPE = 4
const USER_CENTER_WECHAT_STATE_PREFIX = 'CherryUserwxCode'

const createDefaultState = (): AuthState => ({
  isLoggedIn: false,
  token: null,
  expiresAt: null,
  user: null,
})

type LoginCallback = (payload: unknown) => void
type UserCenterTheme = 'istone' | 'iyx' | 'carotmas'

interface PendingLogin {
  resolve: (code: string) => void
  reject: (error: Error) => void
  timer: ReturnType<typeof setTimeout>
  stopCloseWatcher?: () => void
}

interface CherryUserInstance {
  login: () => void
  weblogout?: () => void | Promise<void>
  setStyle?: (style: Record<string, string>) => void
  onLoginSuccessed?: (callback: LoginCallback) => void
  getUserState?: (callback: () => boolean) => void
  onLogout?: (callback: () => void) => void
}

interface UserCenterMessage {
  type?: number
  isReturnMsg?: boolean
}

interface CherryUserConstructor {
  new (appId: string, options: { theme: UserCenterTheme; lang: 'zh' | 'en' }): CherryUserInstance
}

declare global {
  interface Window {
    CherryUser?: CherryUserConstructor
  }
}

class AuthService {
  state = ref<AuthState>(createDefaultState())
  authRecoveryPromptVisible = ref(false)
  authRecoveryRefreshing = ref(false)
  private initPromise: Promise<void> | null = null
  private sdkLoadPromise: Promise<void> | null = null
  private userCenterApp: CherryUserInstance | null = null
  private userCenterKey = ''
  private pendingLogin: PendingLogin | null = null
  private pendingLoginPromise: Promise<string> | null = null
  private userCenterLoginPromise: Promise<AuthState> | null = null
  private userCenterSyncPromise: Promise<AuthState> | null = null
  private authExpiredPromise: Promise<void> | null = null
  private isLoggingOut = false
  private messageGuardInstalled = false
  private requiresAuthRecovery = false

  constructor() {
    setAuthExpiredHandler(() => this.handleAuthExpired())
  }

  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise
    this.initPromise = this.loadFromStorage()
    return this.initPromise
  }

  installUserCenterMessageGuard(): void {
    if (this.messageGuardInstalled) return
    this.messageGuardInstalled = true
    window.addEventListener('message', this.guardUserCenterRawUrlMessage, true)
  }

  preloadUserCenter(): void {
    void this.init()
      .then(() => this.loadUserCenterSdk())
      .then(() => {
        this.getUserCenterApp()
      })
      .catch(() => {
        // The click path still reports actionable SDK errors.
      })
  }

  async loginWithUserCenter(): Promise<AuthState> {
    if (this.userCenterLoginPromise) {
      this.reopenPendingUserCenter()
      return this.userCenterLoginPromise
    }

    this.userCenterLoginPromise = this.performUserCenterLogin().finally(() => {
      this.userCenterLoginPromise = null
    })
    return this.userCenterLoginPromise
  }

  async handleWechatLoginRedirect(): Promise<void> {
    await this.init()
    if (!this.hasWechatLoginCallback() || (this.state.value.isLoggedIn && this.state.value.token && !this.isExpired())) {
      return
    }

    await this.loginWithUserCenter()
    this.clearWechatLoginCallback()
  }

  private async performUserCenterLogin(): Promise<AuthState> {
    await this.init()
    const code = await this.requestUserCenterCode()
    return await this.loginWithUserCenterCode(code)
  }

  private async loginWithUserCenterCode(code: string): Promise<AuthState> {
    const data = await this.request<DriverLoginResponse>('/api/user/login', {
      method: 'POST',
      body: { code },
    })
    return this.saveLoginResponse(data)
  }

  applyDevLoginResponse(data: DriverLoginResponse): AuthState {
    if (!IS_INTERNAL_BUILD) {
      throw new ApiError('FORBIDDEN', 'Dev login state is not available in production')
    }
    return this.saveLoginResponse(data)
  }

  private saveLoginResponse(data: DriverLoginResponse): AuthState {
    const next: AuthState = {
      isLoggedIn: true,
      token: data.accessToken.accessToken,
      expiresAt: data.accessToken.expiresAt,
      user: {
        id: String(data.userInfo.id),
        nickname: data.userInfo.name,
        avatar: data.userInfo.avatar,
        createdAt: data.userInfo.createdAt,
      },
    }
    this.saveState(next)
    return next
  }

  private async syncUserCenterLogin(code: string): Promise<AuthState> {
    if (this.userCenterSyncPromise) return this.userCenterSyncPromise
    this.userCenterSyncPromise = this.loginWithUserCenterCode(code).finally(() => {
      this.userCenterSyncPromise = null
    })
    return this.userCenterSyncPromise
  }

  async openUserCenter(): Promise<void> {
    await this.init()
    if (!this.state.value.isLoggedIn || !this.state.value.token || this.isExpired()) {
      void this.loginWithUserCenter().catch((error) => {
        console.warn('user center login failed', error)
      })
      return
    }

    await this.loadUserCenterSdk()
    this.openUserCenterApp(this.getUserCenterApp())
  }

  async logout(notifyServer = true): Promise<void> {
    if (this.isLoggingOut) return
    this.isLoggingOut = true
    try {
      if (notifyServer && this.state.value.token) {
        try {
          await this.request<unknown>('/api/user/logout', { method: 'POST' })
        } catch {
          // Local logout should still complete if the server is unavailable.
        }
      }
      this.clearLocalSession()
      this.rejectPendingLogin(new ApiError('CANCELLED', 'User cancelled login'))
      await this.logoutUserCenter()
      this.reloadPage()
    } finally {
      this.isLoggingOut = false
    }
  }

  clearLocalSession(): void {
    localStorage.removeItem(AUTH_STORAGE_KEY)
    this.state.value = createDefaultState()
  }

  async handleAuthExpired(): Promise<void> {
    if (this.isLoggingOut) return
    if (this.authExpiredPromise) return this.authExpiredPromise
    this.authExpiredPromise = this.performAuthExpired().finally(() => {
      this.authExpiredPromise = null
    })
    return this.authExpiredPromise
  }

  private async performAuthExpired(): Promise<void> {
    this.isLoggingOut = true
    try {
      this.clearLocalSession()
      this.rejectPendingLogin(new ApiError('CANCELLED', 'User cancelled login'))
      this.requiresAuthRecovery = true
      this.authRecoveryPromptVisible.value = true
    } finally {
      this.isLoggingOut = false
    }
  }

  async confirmAuthRecovery(): Promise<void> {
    if (!this.requiresAuthRecovery || this.isLoggingOut) return
    this.isLoggingOut = true
    this.authRecoveryRefreshing.value = true
    try {
      await Promise.race([
        this.logoutUserCenter(),
        new Promise<void>((resolve) => window.setTimeout(resolve, AUTH_RECOVERY_LOGOUT_TIMEOUT_MS)),
      ])
    } finally {
      this.reloadPage()
    }
  }

  async requireLogin(): Promise<AuthState> {
    await this.init()
    if (this.requiresAuthRecovery) {
      this.authRecoveryPromptVisible.value = true
      throw new ApiError('AUTH_EXPIRED', 'Page refresh is required before logging in again')
    }
    if (this.state.value.isLoggedIn && this.state.value.token && !this.isExpired()) {
      return this.state.value
    }
    if (this.state.value.token && this.isExpired()) {
      await this.logout(false)
    }
    return await this.loginWithUserCenter()
  }

  getAuthHeaders(): Record<string, string> {
    if (!this.state.value.token || this.isExpired()) return {}
    return {
      Authorization: `token ${this.state.value.token}`,
    }
  }

  private async loadFromStorage(): Promise<void> {
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw) as AuthState
      if (parsed?.token && parsed?.user && !this.isExpired(parsed)) {
        this.state.value = parsed
      } else {
        localStorage.removeItem(AUTH_STORAGE_KEY)
      }
    } catch {
      localStorage.removeItem(AUTH_STORAGE_KEY)
      this.state.value = createDefaultState()
    }
  }

  private saveState(next: AuthState) {
    this.state.value = next
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(next))
  }

  private isExpired(state = this.state.value): boolean {
    if (!state.expiresAt) return false
    return state.expiresAt * 1000 <= Date.now()
  }

  private async request<T>(path: string, options: { method: 'GET' | 'POST'; body?: unknown }): Promise<T> {
    return await apiRequest<T>(path, {
      method: options.method,
      headers: this.getAuthHeaders(),
      body: options.body,
    })
  }

  private loadUserCenterSdk(): Promise<void> {
    if (this.sdkLoadPromise) return this.sdkLoadPromise
    this.sdkLoadPromise = window.CherryUser ? Promise.resolve() : Promise.reject(new ApiError('UNKNOWN', 'User center SDK is unavailable'))
    return this.sdkLoadPromise
  }

  private async requestUserCenterCode(): Promise<string> {
    await this.loadUserCenterSdk()
    const app = this.getUserCenterApp()

    if (this.pendingLoginPromise) {
      this.openUserCenterApp(app)
      return this.pendingLoginPromise
    }

    this.pendingLoginPromise = new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pendingLogin?.resolve === resolve) {
          this.rejectPendingLogin(new ApiError('TIMEOUT', 'User center login timed out'))
        }
      }, LOGIN_TIMEOUT_MS)

      this.pendingLogin = {
        resolve,
        reject,
        timer,
      }
      this.pendingLogin.stopCloseWatcher = this.watchUserCenterClose(resolve)

      try {
        this.openUserCenterApp(app)
      } catch (error) {
        this.rejectPendingLogin(error instanceof Error ? error : new Error('user center login failed'))
      }
    })
    return this.pendingLoginPromise
  }

  private getUserCenterApp(): CherryUserInstance {
    const config = this.getUserCenterConfig()
    const key = `${config.appId}:${config.theme}:${config.lang}`
    if (this.userCenterApp && this.userCenterKey === key) {
      return this.userCenterApp
    }

    const CherryUser = window.CherryUser
    if (!CherryUser) throw new ApiError('UNKNOWN', 'User center SDK is unavailable')

    document.body.setAttribute('data-theme-style', config.theme)
    this.userCenterApp = new CherryUser(config.appId, {
      theme: config.theme,
      lang: config.lang,
    })
    this.userCenterKey = key
    this.bindUserCenterEvents(this.userCenterApp)
    return this.userCenterApp
  }

  private bindUserCenterEvents(app: CherryUserInstance) {
    app.onLoginSuccessed?.((payload) => {
      const code = this.extractCode(payload)
      if (!code || this.isLoggingOut) return
      if (!this.pendingLogin) {
        if (this.state.value.isLoggedIn && this.state.value.token && !this.isExpired()) return
        if (this.userCenterLoginPromise) return
        void this.syncUserCenterLogin(code).catch((error) => {
          console.warn('sync user center login failed', error)
        })
        return
      }
      const pending = this.pendingLogin
      clearTimeout(pending.timer)
      pending.stopCloseWatcher?.()
      this.pendingLogin = null
      this.pendingLoginPromise = null
      pending.resolve(code)
    })

    app.getUserState?.(() => Boolean(this.state.value.isLoggedIn && this.state.value.token && !this.isExpired()))

    app.onLogout?.(() => {
      if (this.isLoggingOut) return
      this.clearLocalSession()
      this.rejectPendingLogin(new ApiError('CANCELLED', 'User cancelled login'))
      this.reloadPage()
    })
  }

  private async logoutUserCenter(): Promise<void> {
    let app = this.userCenterApp
    if (!app) {
      try {
        await this.loadUserCenterSdk()
        app = this.getUserCenterApp()
      } catch {
        return
      }
    }
    if (!app.weblogout) return
    try {
      await app.weblogout()
    } catch {
      // Local logout is authoritative for the driver UI.
    }
  }

  private getUserCenterConfig(): { appId: string; theme: UserCenterTheme; lang: 'zh' | 'en' } {
    const currentBrand = brand.value
    const appIds: Record<Brand, string> = {
      [Brand.irok]: import.meta.env.VITE_USER_CENTER_APP_ID_IROK || import.meta.env.VITE_USER_CENTER_APP_ID || DEFAULT_USER_CENTER_APP_ID,
      [Brand.iyx]: import.meta.env.VITE_USER_CENTER_APP_ID_IYX || import.meta.env.VITE_USER_CENTER_APP_ID || DEFAULT_USER_CENTER_APP_ID,
      [Brand.carotmas]: import.meta.env.VITE_USER_CENTER_APP_ID_CAROTMAS || import.meta.env.VITE_USER_CENTER_APP_ID || DEFAULT_USER_CENTER_APP_ID,
    }
    const themes: Record<Brand, UserCenterTheme> = {
      [Brand.irok]: 'istone',
      [Brand.iyx]: 'iyx',
      [Brand.carotmas]: 'carotmas',
    }
    const locale = String(i18n.global.locale || '').toLowerCase()

    return {
      appId: appIds[currentBrand] || DEFAULT_USER_CENTER_APP_ID,
      theme: themes[currentBrand] || 'istone',
      lang: locale.startsWith('zh') ? 'zh' : 'en',
    }
  }

  private applyUserCenterStyle() {
    const app = this.userCenterApp
    if (!app?.setStyle) return

    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
    app.setStyle(
      isMobile
        ? {
            display: 'block',
            position: 'fixed',
            'z-index': String(zIndexLayers.userCenter),
          }
        : {
            display: 'block',
            width: '400px',
            height: '600px',
            position: 'fixed',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            'z-index': String(zIndexLayers.userCenter),
          },
    )
  }

  private openUserCenterApp(app: CherryUserInstance) {
    app.login()
    this.applyUserCenterStyle()
  }

  private reopenPendingUserCenter() {
    if (!this.pendingLoginPromise) return
    try {
      this.openUserCenterApp(this.getUserCenterApp())
    } catch (error) {
      console.warn('reopen user center failed', error)
    }
  }

  private rejectPendingLogin(error: Error) {
    if (!this.pendingLogin) return
    const pending = this.pendingLogin
    clearTimeout(pending.timer)
    pending.stopCloseWatcher?.()
    this.pendingLogin = null
    this.pendingLoginPromise = null
    pending.reject(error)
  }

  private watchUserCenterClose(resolve: (code: string) => void): () => void {
    const stopDomWatcher = this.watchUserCenterDomClose(resolve)
    const stopMessageWatcher = this.watchUserCenterCloseMessage(resolve)

    return () => {
      stopDomWatcher()
      stopMessageWatcher()
    }
  }

  private watchUserCenterDomClose(resolve: (code: string) => void): () => void {
    let sawLoginUi = false
    let stopped = false

    const stop = () => {
      stopped = true
      observer.disconnect()
      window.clearTimeout(initialTimer)
      window.clearInterval(intervalTimer)
    }

    const check = () => {
      if (stopped || this.pendingLogin?.resolve !== resolve) return
      const visible = this.hasVisibleUserCenterUi()
      sawLoginUi = sawLoginUi || visible
      if (!sawLoginUi || visible) return
      this.rejectPendingLogin(new ApiError('CANCELLED', 'User cancelled login'))
    }

    const observer = new MutationObserver(check)
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      childList: true,
      subtree: true,
    })

    const initialTimer = window.setTimeout(check, 300)
    const intervalTimer = window.setInterval(check, 500)
    return stop
  }

  private watchUserCenterCloseMessage(resolve: (code: string) => void): () => void {
    const listener = (event: MessageEvent) => {
      if (this.pendingLogin?.resolve !== resolve || !this.isUserCenterCloseMessage(event)) return
      this.rejectPendingLogin(new ApiError('CANCELLED', 'User cancelled login'))
    }

    window.addEventListener('message', listener)
    return () => {
      window.removeEventListener('message', listener)
    }
  }

  private hasVisibleUserCenterUi(): boolean {
    const items = Array.from(document.body.querySelectorAll<HTMLElement>('iframe, [class], [id]'))
    return items.some((item) => {
      const marker = `${item.id} ${item.className} ${item.getAttribute('src') || ''}`.toLowerCase()
      if (!marker.includes('cherry') && !marker.includes('usercenter') && !marker.includes('user-center') && !marker.includes('joyway')) return false
      const rect = item.getBoundingClientRect()
      const style = window.getComputedStyle(item)
      return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity || '1') > 0
    })
  }

  private extractCode(payload: unknown): string | null {
    if (typeof payload === 'string' && payload.trim()) return payload.trim()
    if (!payload || typeof payload !== 'object') return null
    const item = payload as Record<string, unknown>
    const direct = item.code || item.authCode
    if (typeof direct === 'string' && direct.trim()) return direct.trim()
    const data = item.data
    if (data && typeof data === 'object') {
      const dataCode = (data as Record<string, unknown>).code
      if (typeof dataCode === 'string' && dataCode.trim()) return dataCode.trim()
    }
    return null
  }

  private hasWechatLoginCallback(): boolean {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state')
    return Boolean(code && state?.startsWith(USER_CENTER_WECHAT_STATE_PREFIX))
  }

  private clearWechatLoginCallback() {
    const url = new URL(window.location.href)
    url.searchParams.delete('code')
    url.searchParams.delete('state')
    window.history.replaceState(window.history.state, document.title, `${url.pathname}${url.search}${url.hash}`)
  }

  private reloadPage() {
    window.location.reload()
  }

  private guardUserCenterRawUrlMessage = (event: MessageEvent) => {
    if (!this.isUserCenterRawUrlMessage(event)) return
    event.stopImmediatePropagation()
  }

  private isUserCenterCloseMessage(event: MessageEvent): boolean {
    const data = this.parseUserCenterMessage(event.data)
    return data?.type === USER_CENTER_CLOSE_MESSAGE_TYPE && (data.isReturnMsg === undefined || data.isReturnMsg === true)
  }

  private parseUserCenterMessage(data: unknown): UserCenterMessage | null {
    if (typeof data === 'string') {
      try {
        return this.parseUserCenterMessage(JSON.parse(data))
      } catch {
        return null
      }
    }

    if (!data || typeof data !== 'object') return null
    return data as UserCenterMessage
  }

  private isUserCenterRawUrlMessage(event: MessageEvent): boolean {
    if (typeof event.data !== 'string') return false
    const value = event.data.trim()
    if (!/^https?:\/\//i.test(value)) return false

    try {
      const messageUrl = new URL(value)
      const originUrl = event.origin && event.origin !== 'null' ? new URL(event.origin) : null
      return messageUrl.hostname.endsWith('joyway.net') || messageUrl.searchParams.get('state')?.startsWith(USER_CENTER_WECHAT_STATE_PREFIX) || Boolean(originUrl?.hostname.endsWith('joyway.net'))
    } catch {
      return false
    }
  }
}

export const authService = new AuthService()
